import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { fetchUserOrders, finishOrder } from '../../services/screenApi';
import { clearScreenAuthData, getScreenUsername, persistScreenUsername, clearScreenUsername } from '../../utils/screenAuthStorage';
import useScreenAuth from '../../components/useScreenAuth';
import Modal from '../../components/Modal';

const ScreenUserOrdersView = () => {
  const [username, setUsername] = useState(() => getScreenUsername() || '');
  const [isUsernameSet, setIsUsernameSet] = useState(() => !!getScreenUsername());
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());
  const navigate = useNavigate();
  const { token, isAuthenticated, isLoading: isAuthLoading, views } = useScreenAuth();
  const intervalRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [modalImage, setModalImage] = useState('');
  const imageModalRef = useRef(null);
  const modalTimeoutRef = useRef(null);

  const hasUserOrdersAccess = views?.includes('user') && views?.includes('orders');

  const handleDisconnect = () => {
    try {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } catch (e) {}
    clearScreenUsername();
    clearScreenAuthData();
    navigate('/screens/login');
  };

  const handleUsernameSubmit = (e) => {
    e.preventDefault();
    if (!username.trim()) {
      toast.error('Veuillez saisir votre nom d\'utilisateur');
      return;
    }
    persistScreenUsername(username.trim());
    setIsUsernameSet(true);
    loadOrders();
  };

  const handleChangeUser = () => {
    clearScreenUsername();
    setUsername('');
    setIsUsernameSet(false);
    setOrders([]);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const resolveImageUrl = (path = '') => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const base = process.env?.REACT_APP_DATA_PIC_PATH;
    return base ? `${base}/${path}` : path;
  };

  const cleanupImageModalArtifacts = () => {
    const modalElement = document.getElementById('showModal');
    if (modalElement) {
      modalElement.classList.remove('show');
      modalElement.style.display = 'none';
      modalElement.setAttribute('aria-hidden', 'true');
      modalElement.removeAttribute('aria-modal');
      modalElement.removeAttribute('role');
    }

    if (!document.querySelector('.modal.show')) {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.querySelectorAll('.modal-backdrop').forEach((backdrop) => backdrop.remove());
    }
  };

  const closeImageModal = () => {
    if (modalTimeoutRef.current) {
      clearTimeout(modalTimeoutRef.current);
      modalTimeoutRef.current = null;
    }

    const modalElement = document.getElementById('showModal');
    try {
      const instance = modalElement && window.bootstrap
        ? window.bootstrap.Modal.getInstance(modalElement)
        : imageModalRef.current;
      if (instance) {
        instance.hide();
      }
    } catch (e) {}

    setShowModal(false);
    window.setTimeout(cleanupImageModalArtifacts, 350);
  };

  const handleImageModalHidden = () => {
    if (modalTimeoutRef.current) {
      clearTimeout(modalTimeoutRef.current);
      modalTimeoutRef.current = null;
    }
    setShowModal(false);
    cleanupImageModalArtifacts();
  };

  const openImage = (url) => {
    const resolvedUrl = resolveImageUrl(url);
    if (!resolvedUrl) return;
    
    if (modalTimeoutRef.current) {
      clearTimeout(modalTimeoutRef.current);
    }

    setModalImage(resolvedUrl);
    setShowModal(true);
    
    setTimeout(() => {
      const modalElement = document.getElementById('showModal');
      if (modalElement) {
        imageModalRef.current = window.bootstrap.Modal.getOrCreateInstance(modalElement);
        imageModalRef.current.show();
        
        modalTimeoutRef.current = setTimeout(() => {
          closeImageModal();
        }, 300000);
      }
    }, 50);
  };

  const loadOrders = async () => {
    if (!token || !username) return;
    
    try {
      const data = await fetchUserOrders(token, username);
      const normalized = Array.isArray(data) ? data : data?.orders || data?.response || [];
      setOrders([...(normalized || [])]);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      if (error?.response?.status === 401) {
        clearScreenAuthData();
        navigate('/screens/login');
      } else if (error?.response?.status === 404) {
        toast.error('Utilisateur introuvable');
      } else {
        toast.error('Erreur lors du chargement des commandes');
        console.error('Unable to fetch user orders', error);
      }
    }
  };

  const handleFinishOrder = async (orderId, orderCode) => {
    if (!window.confirm(`Confirmer que la commande ${orderCode} est prête ?`)) {
      return;
    }

    try {
      await finishOrder(token, orderId);
      toast.success('Commande marquée comme prête');
      loadOrders();
    } catch (error) {
      if (error?.response?.status === 401) {
        clearScreenAuthData();
        navigate('/screens/login');
      } else if (error?.response?.status === 404) {
        toast.error('Commande introuvable');
      } else {
        toast.error('Erreur lors de la finalisation de la commande');
        console.error('Unable to finish order', error);
      }
    }
  };

  const safeParseDate = (v) => {
    if (!v) return null;
    try {
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? null : d;
    } catch (e) {
      return null;
    }
  };

  const getCustomerPreference = (order) => {
    if (!order) return null;

    const directValue = order.customer_preference
      ?? order.customerPreference
      ?? order.customer_data?.customer_preference
      ?? order.customer_data?.customerPreference
      ?? order.meta_data?.customer_preference
      ?? order.meta_data?.customerPreference
      ?? order.meta_data?.woocommerce?.customer_preference
      ?? order.meta_data?.woocommerce?.customerPreference;

    if (directValue !== undefined && directValue !== null && String(directValue).trim() !== '') {
      return directValue;
    }

    const meta = Array.isArray(order.meta_data) ? order.meta_data : [];
    const preferenceMeta = meta.find((item) => {
      const key = String(item?.key || '').toLowerCase();
      return key === 'customer_preference' || key === '_customer_preference' || key === 'customer preference';
    });

    return preferenceMeta?.value ?? null;
  };

  const formatCustomerPreference = (value) => {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) {
      return value.map((entry) => formatCustomerPreference(entry)).filter(Boolean).join('\n');
    }
    if (typeof value === 'object') {
      return Object.entries(value)
        .filter(([, entryValue]) => entryValue !== null && entryValue !== undefined && String(entryValue).trim() !== '')
        .map(([key, entryValue]) => `${key}: ${typeof entryValue === 'object' ? JSON.stringify(entryValue) : entryValue}`)
        .join('\n');
    }
    return String(value).trim();
  };

  const renderCustomerPreferenceFlag = (order) => {
    const customerPreference = formatCustomerPreference(getCustomerPreference(order));
    if (!customerPreference) return null;

    return (
      <span
        className="badge bg-danger ms-2 align-middle"
        title={customerPreference}
        style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}
      >
        <i className="fas fa-exclamation-triangle me-1"></i>
        Demande Client
      </span>
    );
  };

  const getOrderDeadline = (order) => {
    const committed = order?.customer_data?.committed_pickup_time || null;
    const committedDate = safeParseDate(committed);
    if (committedDate) return committedDate;

    const created = order?.createdAt?.$date || order?.createdAt || null;
    const createdDate = safeParseDate(created);
    if (createdDate) {
      return new Date(createdDate.getTime() + 8 * 60 * 1000);
    }

    return null;
  };

  const formatCountdown = (ms) => {
    if (ms === null) return '—';
    const negative = ms < 0;
    const absMs = Math.abs(ms);
    const totalSeconds = Math.floor(absMs / 1000);
    const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
    const seconds = (totalSeconds % 60).toString().padStart(2, '0');
    return `${negative ? '-' : ''}${minutes}:${seconds}`;
  };

  useEffect(() => {
    if (!isAuthLoading && !isAuthenticated) {
      navigate('/screens/login');
    }
  }, [isAuthLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthLoading && isAuthenticated && !hasUserOrdersAccess) {
      toast.error('Accès non autorisé');
      navigate('/screens/login');
    }
  }, [isAuthLoading, isAuthenticated, hasUserOrdersAccess, navigate]);

  useEffect(() => {
    if (!token || !isUsernameSet || !username) {
      return undefined;
    }

    loadOrders();
    intervalRef.current = setInterval(loadOrders, 10000);
    return () => clearInterval(intervalRef.current);
  }, [token, isUsernameSet, username]);

  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    return () => {
      if (modalTimeoutRef.current) {
        clearTimeout(modalTimeoutRef.current);
      }
      
      if (imageModalRef.current) {
        try {
          imageModalRef.current.hide();
          imageModalRef.current.dispose();
        } catch (e) {}
        imageModalRef.current = null;
      }
      
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      const backdrops = document.querySelectorAll('.modal-backdrop');
      backdrops.forEach((backdrop) => backdrop.remove());
    };
  }, []);

  if (isAuthLoading || isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (!isUsernameSet) {
    return (
      <div className="container py-4" style={{ maxWidth: '480px' }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0">Mes Commandes</h2>
          <button 
            type="button" 
            className="btn btn-sm btn-outline-danger"
            onClick={handleDisconnect}
          >
            <i className="fas fa-sign-out-alt"></i> Déconnecter
          </button>
        </div>
        
        <div className="card shadow-sm">
          <div className="card-body p-4">
            <form onSubmit={handleUsernameSubmit}>
              <div className="mb-3">
                <label htmlFor="username" className="form-label fs-5">
                  Nom d'utilisateur
                </label>
                <input
                  id="username"
                  type="text"
                  className="form-control form-control-lg"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Entrez votre nom d'utilisateur"
                  autoFocus
                />
              </div>
              <button type="submit" className="btn btn-primary btn-lg w-100">
                <i className="fas fa-arrow-right me-2"></i>
                Continuer
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="sticky-top bg-white shadow-sm border-bottom">
        <div className="container-fluid p-3">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0">
                <i className="fas fa-user me-2"></i>
                {username}
              </h5>
              <small className="text-muted">{orders.length} commande(s)</small>
            </div>
            <div className="btn-group">
              <button 
                type="button" 
                className="btn btn-sm btn-outline-secondary"
                onClick={handleChangeUser}
              >
                <i className="fas fa-user-edit"></i>
              </button>
              <button 
                type="button" 
                className="btn btn-sm btn-outline-danger"
                onClick={handleDisconnect}
              >
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {!orders.length ? (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 'calc(100vh - 120px)' }}>
          <div className="text-center p-4">
            <i className="fas fa-inbox fa-4x text-muted mb-3"></i>
            <h4 className="text-muted">Aucune commande</h4>
            <p className="text-muted">Les commandes apparaîtront automatiquement</p>
          </div>
        </div>
      ) : (
        <div className="container-fluid p-3 pb-5">
          <div className="row g-3">
            {orders.map((order) => {
              const productType = (order?.productType || '').toLowerCase();
              const isKooulOrder = productType === 'kooul';
              const isSiteOrder = productType === 'site';
              const isSistersOrder = productType === 'glovo_sisters';
              const products = Array.isArray(order?.products) ? order.products : [];

              if (!products.length) return null;

              const deadline = getOrderDeadline(order);
              const remainingMs = deadline ? deadline.getTime() - nowTick : null;
              const formatted = formatCountdown(remainingMs);
              const isOverdue = remainingMs < 0;

              let bgColorClass = 'bg-secondary';
              let borderColorClass = 'border-secondary';
              let bodyBgColor = '#f8f9fa';
              let headerInlineStyle = null;
              let cardBorderInline = null;

              if (isKooulOrder) {
                headerInlineStyle = { background: '#8B0000', color: '#fff' };
                cardBorderInline = { borderColor: '#8B0000' };
                bodyBgColor = '#ffebee';
                borderColorClass = '';
                bgColorClass = '';
              } else if (isSiteOrder) {
                bgColorClass = 'bg-info';
                borderColorClass = 'border-info';
                bodyBgColor = '#cfe2ff';
              } else if (productType === 'glovo') {
                bgColorClass = 'bg-warning';
                borderColorClass = 'border-warning';
                bodyBgColor = '#fff3cd';
              } else if (isSistersOrder) {
                headerInlineStyle = { background: '#ffb585', color: '#000' };
                cardBorderInline = { borderColor: '#ffb585' };
                bodyBgColor = '#fff8f0';
                borderColorClass = '';
                bgColorClass = '';
              }

              return (
                <div key={order._id || order.orderCode} className="col-12">
                  <div 
                    className={`card shadow-sm border-3 ${borderColorClass}`} 
                    style={{ ...(cardBorderInline || {}), borderWidth: '3px' }}
                  >
                    <div 
                      className={`card-header ${isSistersOrder || isKooulOrder ? '' : `${bgColorClass} text-white`} d-flex justify-content-between align-items-center py-3`}
                      style={headerInlineStyle || undefined}
                    >
                      <div>
                        <span className="fw-bold fs-4">#{order.orderNumber || order.orderCode}</span>
                        {order.CustomerName && (
                          <div className="mt-1">
                            <small><i className="fas fa-user me-1"></i>{order.CustomerName}</small>
                          </div>
                        )}
                      </div>
                      {deadline && (
                        <span 
                          className={`badge ${isOverdue ? 'bg-danger' : 'bg-primary'}`} 
                          style={{ fontSize: '1.2rem', padding: '0.6rem 0.9rem' }}
                        >
                          <i className="fas fa-clock me-1"></i>
                          {formatted}
                        </span>
                      )}
                    </div>
                    
                    <div className="card-body p-3" style={{ backgroundColor: bodyBgColor }}>
                      {products.map((product, index) => {
                        const resolvedImageUrl = resolveImageUrl(product?.urlImage || product?.image);
                        const quantityLabel = product?.quantity?.toString().replace('x', '') || '';
                        const productName = product?.title || product?.name || '';
                        const displayTitle = `${quantityLabel ? `${quantityLabel}x ` : ''}${productName}`.trim() || 'Produit';

                        return (
                          <div key={index} className="mb-3 pb-3 border-bottom">
                            <div className="d-flex gap-3">
                              {resolvedImageUrl && (
                                <div 
                                  style={{ 
                                    width: '80px', 
                                    height: '80px', 
                                    flexShrink: 0,
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => openImage(product?.urlImage)}
                                >
                                  <img 
                                    src={resolvedImageUrl} 
                                    alt={displayTitle}
                                    className="img-fluid rounded"
                                    style={{ 
                                      width: '100%', 
                                      height: '100%', 
                                      objectFit: 'cover' 
                                    }}
                                  />
                                </div>
                              )}
                              <div className="flex-grow-1">
                                <h6 className="mb-1 fw-bold">
                                  {displayTitle}
                                  {renderCustomerPreferenceFlag(order)}
                                </h6>
                                {product.options?.length > 0 && (
                                  <div className="mt-1">
                                    {product.options.map((option, idx) => (
                                      <div key={idx} className="text-muted small">
                                        <i className="fas fa-plus me-1"></i>
                                        {option.quantity || 1}x {option.title}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="card-footer bg-white p-3">
                      <button 
                        className="btn btn-success btn-lg w-100 fw-bold"
                        style={{ fontSize: '1.2rem', padding: '1rem' }}
                        onClick={() => handleFinishOrder(order._id, order.orderNumber || order.orderCode)}
                      >
                        <i className="fas fa-check-circle me-2"></i>
                        Marquer comme prête
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Modal modalId="showModal" modalType="modal-lg" onClose={handleImageModalHidden}>
        <div className="text-center">
          {modalImage ? (
            <img src={modalImage} alt="Large Preview" className="img-fluid" />
          ) : (
            <p className="text-muted">Aucune image disponible</p>
          )}
        </div>
      </Modal>
    </>
  );
};

export default ScreenUserOrdersView;
